import TiktokPixel, { AdvancedMatching, Options } from "tiktok-pixel";
import { SubscriptionPlan, UserInfo } from "../../components/signup/SignUpApi";
import { CheckoutLine, StoreCheckout } from "../../components/checkout/Checkout.types";
import { isTrackingAllowed } from "./consent";
import { TrackedProduct } from "./types";

const pixelIdFromConfig = process.env.REACT_APP_TIKTOK_PIXEL_ID || undefined;

/** TikTok's `content_type` for a single item, as opposed to `product_group`. */
const CONTENT_TYPE_PRODUCT = "product";

/**
 * The pixel's own queue, which the SDK installs on `window`.
 *
 * `tiktok-pixel` only re-exports `init`, `pageView` and `track`, so `identify`
 * has to be reached directly. That matters more than it sounds: the package's
 * `init` short-circuits when `window.ttq` already exists, which means calling
 * `init(userInfo)` a second time to attach the customer details does nothing
 * at all. Advanced matching has to go through `identify`.
 */
type TiktokQueue = {
  identify: (data: Record<string, string>) => void;
  page: () => void;
};

const getQueue = (): TiktokQueue | undefined => (window as any).ttq;

export const getStoreCheckoutContents = (checkout: StoreCheckout): any => {
  const contents: any = [];
  const content_ids: string[] = [];
  checkout?.lines.forEach((line: CheckoutLine) => {
    const { sku, product, pricing } = line?.variant ?? {};
    const { id, name } = product ?? {};
    const { price } = pricing ?? {};
    if (id) {
      content_ids.push(id);
      contents.push({ content_id: id, content_name: name, description: sku, value: price.gross.amount });
    }
  });
  return { contents, content_ids };
};

export const buildPaymentDataFromStoreCheckout = (checkout: StoreCheckout): any => {
  const { contents } = getStoreCheckoutContents(checkout);
  const { amount, currency } = checkout?.totalPrice?.gross ?? {};
  return {
    value: amount?.toFixed(2),
    currency: currency,
    content_type: CONTENT_TYPE_PRODUCT,
    contents,
  };
};

export const buildProductData = (product: TrackedProduct): any => {
  const { id, name, price, currency, quantity } = product;
  return {
    content_type: CONTENT_TYPE_PRODUCT,
    contents: [
      {
        content_id: id,
        content_type: CONTENT_TYPE_PRODUCT,
        content_name: name,
        price,
        quantity: quantity ?? 1,
      },
    ],
    /* `value` is what the whole event is worth, so it multiplies out. */
    value: price != null ? (price * (quantity ?? 1)).toFixed(2) : undefined,
    currency,
  };
};

class TiktokPixelAPI {
  /** Set by the cookie banner. Consent can also arrive as a stored cookie. */
  private _isAllowed = false;
  private _isLoaded = false;

  /**
   * Neither app asks for consent, so this is true for every visitor — see
   * `consent.ts` for the decision and the one switch that reverses it.
   *
   * `_isAllowed` is still passed through rather than ignored: it is how an
   * in-session grant would reach this class if a banner were ever put back,
   * and it arrives before the cookie can be read.
   */
  private get isAllowed(): boolean {
    return isTrackingAllowed(this._isAllowed);
  }

  /**
   * Loads the pixel. Safe to call repeatedly and from anywhere — the SDK only
   * injects its script once.
   *
   * `userInfo` is optional so the pixel can also cover anonymous browsing,
   * which is where the top of the funnel (`ViewContent`, `AddToCart`) happens.
   */
  init(userInfo?: UserInfo) {
    /* Consent is checked here rather than only at the call sites, because
       loading the SDK is itself the disclosure: it fires a page view and,
       with `userInfo`, hands TikTok the customer's details. `SignedInContext`
       calls this for every signed-in visitor and has no consent check of its
       own, so gating the entry point is what actually closes that path. */
    if (!pixelIdFromConfig || !this.isAllowed) {
      return;
    }
    const options: Options = {
      debug: false,
    };
    /* Passed on the first load only; every later change goes through
       `identify`, since the SDK ignores this argument once loaded. */
    const advancedMatching = this.buildAdvancedMatching(userInfo);
    TiktokPixel.init(pixelIdFromConfig, advancedMatching as AdvancedMatching | undefined, options);
    this._isLoaded = true;

    if (userInfo) {
      this.identify(userInfo);
    }
  }

  /**
   * Attaches the customer's details to subsequent events.
   *
   * TikTok's own instructions ask for these SHA-256 hashed client-side. Bits
   * has decided to send them in the clear, so they go as-is; the empty fields
   * are dropped rather than sent blank, because a hash or value of nothing
   * still counts against the pixel's match rate.
   */
  identify(userInfo?: UserInfo) {
    const data = this.buildAdvancedMatching(userInfo);
    if (!data) {
      return;
    }
    getQueue()?.identify(data);
  }

  private buildAdvancedMatching(userInfo?: UserInfo): Record<string, string> | undefined {
    if (!userInfo) {
      return undefined;
    }
    const { email, phoneNumber, membershipNumber } = userInfo;
    const data: Record<string, string> = {};
    if (email) {
      data.email = email;
    }
    if (phoneNumber) {
      data.phone_number = phoneNumber;
    }
    /* A membership number matches as well as an email without being one. */
    if (membershipNumber) {
      data.external_id = membershipNumber;
    }
    return Object.keys(data).length > 0 ? data : undefined;
  }

  allowTracking() {
    this._isAllowed = true;
  }

  /**
   * A route change in a single-page app is a page view, and the SDK only sends
   * one automatically — the one at load.
   */
  pageView() {
    /* The SDK reaches straight into `window.ttq` here without checking it is
       there, unlike `track`, so this is the one call that has to know whether
       anything actually loaded. Without the guard every route change throws in
       any environment with no pixel configured. */
    if (!this.canTrack() || !this._isLoaded) return;
    TiktokPixel.pageView();
  }

  trackViewContent(product: TrackedProduct) {
    if (!this.canTrack()) return;
    TiktokPixel.track("ViewContent", buildProductData(product));
  }

  trackAddToCart(product: TrackedProduct) {
    if (!this.canTrack()) return;
    TiktokPixel.track("AddToCart", buildProductData(product));
  }

  trackInitiateCheckout(checkout: StoreCheckout) {
    if (!this.canTrack()) return;
    TiktokPixel.track("InitiateCheckout", buildPaymentDataFromStoreCheckout(checkout));
  }

  trackPurchase(checkout: StoreCheckout, userInfo?: UserInfo) {
    if (!this.canTrack()) return;
    this.identify(userInfo);

    const data = buildPaymentDataFromStoreCheckout(checkout);
    /* Both, deliberately. `PlaceAnOrder` is what this has always sent and UK
       campaigns may already be optimising on it; `Purchase` is the event the US
       funnel was set up around. Marketing must pick ONE as the campaign
       conversion — counting both would double every sale. */
    TiktokPixel.track("PlaceAnOrder", data);
    TiktokPixel.track("Purchase", data);
  }

  trackCompleteRegistration(userInfo?: UserInfo) {
    if (!this.canTrack()) return;
    this.identify(userInfo);
    TiktokPixel.track("CompleteRegistration", {});
  }

  trackSubscribe(plan: SubscriptionPlan, userInfo?: UserInfo) {
    if (!this.canTrack()) return;
    this.identify(userInfo);

    const { currency, amount, subscriptionPlanId: id, title } = plan ?? {};
    const amountWithDecimals = amount?.toFixed(2);
    const data = {
      currency,
      value: amountWithDecimals,
      content_type: CONTENT_TYPE_PRODUCT,
      content_id: id,
      content_name: title,
      description: `${title}: ${amountWithDecimals}/month`,
    };
    TiktokPixel.track("Subscribe", data);
  }

  /**
   * Nothing is sent without consent, and nothing can be sent before the SDK is
   * on the page. The load is attempted here rather than assumed, because an
   * event can be the first thing that happens after consent is granted.
   */
  private canTrack(): boolean {
    if (!this.isAllowed) {
      return false;
    }
    /* An event can be the first thing to happen after consent arrives, so the
       load is attempted rather than assumed. `init` is a no-op when no pixel
       is configured. */
    if (!this._isLoaded) {
      this.init();
    }
    /* Deliberately NOT also requiring the SDK to be present: `track` checks
       that itself and quietly does nothing, whereas gating here would stop the
       wrapper calling it at all wherever no pixel id is set — which includes
       every test run, since the CI test job configures no environment at all.
       `pageView` above is the exception, because the SDK does not check. */
    return true;
  }
}

export default new TiktokPixelAPI();
